import {
  type AccountInfo,
  type AuthenticationResult,
  EventType,
  type IPublicClientApplication,
  PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';
import { msalConfig } from '@/authConfig';
import { isRedirectRequest } from '@/lib/isRedirectRequest.ts';
import {
  msalScopes,
  selectAccountAndTryToAcquireTokenFunction,
  silentRequest,
} from '@/lib/selectAccountAndTryToAcquireToken';
import { msalFactory } from '@/mocks/msal';
import { runtimeConfig } from '@/runtimeConfig';

export interface IMsalInstance extends IPublicClientApplication {
  mode: 'production' | 'mock';
  selectAccountAndTryToAcquireToken(): Promise<string | null>;
  getAuthenticatedUser(): Promise<{
    account: AccountInfo;
    scopes: Array<string>;
  } | null>;
}

export const redirectRequest: RedirectRequest = {
  scopes: msalScopes,
  redirectUri: `${globalThis.location.origin}/redirect.html`,
  redirectStartPage: `${globalThis.location.origin}/leads`,
};
class MsalInstance extends PublicClientApplication implements IMsalInstance {
  public mode = 'production' as const;

  public readonly selectAccountAndTryToAcquireToken = () => {
    return selectAccountAndTryToAcquireTokenFunction(this);
  };

  async getAuthenticatedUser(): Promise<{
    account: AccountInfo;
    scopes: Array<string>;
  } | null> {
    const activeAccount = this.getActiveAccount();
    if (!activeAccount) return null;

    const result = await this.acquireTokenSilent(
      silentRequest(activeAccount),
    ).catch(() => null);

    if (result === null) {
      throw new Error('User is not authenticated');
    }

    return {
      account: result.account,
      scopes: result.scopes,
    };
  }
}

export const msalInstance: IMsalInstance =
  runtimeConfig.MsalEnabled.Or('true') === 'false'
    ? msalFactory(msalConfig)
    : new MsalInstance(msalConfig);

export async function initializeMsal(
  instance: IMsalInstance = msalInstance,
): Promise<void> {
  console.log(`Initializing MSAL using ${instance.mode}`);

  instance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const result = event.payload as AuthenticationResult;
      instance.setActiveAccount(result.account);
    }
  });

  await instance.initialize();

  if (isRedirectRequest(globalThis.location)) {
    await instance.handleRedirectPromise();
  }

  const token = await instance.selectAccountAndTryToAcquireToken();

  if (token === null) {
    await instance.loginRedirect({
      ...redirectRequest,
      prompt: 'select_account',
    });
  }
}
